/**
 * The first-sign-in disclaimer: the wording per brand, and the accept gesture.
 *
 * **SweeterTrade and QuFolio** (owner, 2026-10-19); another brand gets its own
 * wording as another entry here. The wording is the owner's, verbatim.
 *
 * **Asked again every 30 days** (owner, 2026-10-19): on the first page load
 * once `EVERY_DAYS` have passed since the account's latest yes. Every yes is
 * kept on the server, so the record shows each time someone agreed.
 *
 * `version` is what the server records an acceptance against
 * (`/disclaimer/{version}`). **Change the wording, change the version** — that
 * asks everyone at once, whatever their 30 days say; an edit under the old
 * version would leave a record saying people agreed to words they never saw.
 *
 * **Use of data and content** (owner, 2026-11-01): every brand's wording ends
 * with the same clause, the brand's display name in it, under its own bold
 * heading (`use`). It came in as new wording, so every version moved on
 * 2026-11-01 and everyone is asked again on their next visit. TrendStrat and a
 * brand with no wording of its own got QuFolio's text under their own name and
 * version on the same day, so the clause reaches them too.
 *
 * Accepting is a deliberate act, in one of three ways (owner, 2026-10-19):
 * - **hover** — on a pointer that can hover, rest on the accept zone for
 *   `HOVER_MS` while its bar fills. A mouse passing over does not agree.
 * - **hold** — on a touch screen, press and hold it for `HOLD_MS`.
 * - **checkbox** — tick "I understand…", then Continue. A click or Enter on the
 *   zone itself is also taken (`click`): it is the plainest yes there is.
 */

import { BRAND } from "./brand-current";

export interface Disclaimer {
  brand: string;
  version: string;
  title: string;
  heading: string;
  lead: string;
  bullets: readonly string[];
  short: readonly string[];
  full: readonly string[];
  /** The use-of-data clause, drawn after `full` as its own paragraph. */
  use: { heading: string; body: string };
  checkbox: string;
}

/** The use-of-data clause's heading, the same for every brand. */
export const USE_HEADING = "Use of data and content.";

/** The use-of-data clause (owner, 2026-11-01; the interface added 2026-11-02), with the brand's name. */
export function useClause(name: string): Disclaimer["use"] {
  return {
    heading: USE_HEADING,
    body:
      `All data, charts, calculations, rankings, screens and analysis on ${name} are provided for your personal, ` +
      "non-commercial use only, inside the app. You may not copy, reproduce, redistribute, resell, publish, " +
      "broadcast, scrape, or use them to build or train any product, service, dataset or model, in whole or in " +
      "part — including by screenshot or screen recording — without our prior written permission. Sharing within " +
      `your ${name} group spaces is permitted. The same applies to the app's interface — the design, layout and ` +
      "arrangement of its views, panels and charts: you may not copy or recreate it, or ask any person or AI tool " +
      "to recreate it. Content may be marked to identify the account it was shown to.",
  };
}

export type AcceptMethod = "hover" | "hold" | "click" | "checkbox";

/** Days an acceptance lasts before the card is shown again. The owner's number. */
export const EVERY_DAYS = 30;

export const HOVER_MS = 1200;
export const HOLD_MS = 1000;

/**
 * QuFolio's own wording (owner, 2026-10-19), written for what QuFolio does — AI
 * notes and chat, third-party data, projections — under a brand's name.
 * "Not a broker-dealer" must change if the brokerage tier ships; change the
 * versions with it.
 */
function researchWording(brand: string, name: string, version: string): Disclaimer {
  return {
    brand,
    version,
    title: `${name} – Important Disclaimer`,
    heading: "FOR EDUCATION AND RESEARCH ONLY – NOT INVESTMENT ADVICE",
    lead: `${name} gives you charts, screeners, indicators, AI-written notes and an AI assistant to help you study markets. Nothing in it tells you what to buy or sell.`,
    bullets: [
      "Tools, not recommendations. Screens, signals, levels and ranks are calculations, not advice.",
      "AI can be wrong. Summaries and chat answers are generated by AI models and may contain errors or out-of-date facts. Check them against the chart and the data.",
      "Data can be delayed or incomplete. Prices, fundamentals and filings come from third-party vendors and may contain errors.",
      "Backtests and projections are hypothetical. Seasonal paths and other forward lines are drawn from history and do not predict what happens next.",
    ],
    short: [
      "Past or simulated performance does not guarantee future results.",
      "You alone are responsible for your trading and investment decisions. Consult a licensed financial professional if you are unsure.",
    ],
    full: [
      `${name} is a software platform for education and market research. All content — charts, indicators, screens, signals, ranks, AI-generated summaries and chat responses, fundamentals, community posts and any other material — is provided for informational and educational purposes only.`,
      `${name} does not provide investment, financial, legal or tax advice, and is not a registered investment adviser or broker-dealer. Nothing on ${name} is a recommendation or solicitation to buy, sell or hold any security or other instrument.`,
      `Market data and fundamentals are provided by third parties and may be delayed, inaccurate or incomplete. AI-generated content may be incorrect. ${name} makes no warranty as to accuracy, completeness or timeliness.`,
      `Trading and investing involve substantial risk, including the loss of more than your initial investment. By continuing, you acknowledge these risks and agree that ${name} and its developers are not liable for any loss arising from your use of the platform.`,
    ],
    use: useClause(name),
    checkbox: `I understand – ${name} is for education and research, not investment advice`,
  };
}

const DISCLAIMERS: Readonly<Record<string, Disclaimer>> = {
  sweetertrade: {
    brand: "sweetertrade",
    version: "st-2026-11-02",
    title: "Sweeter Trade – IMPORTANT DISCLAIMER",
    heading: "NOT INVESTMENT ADVICE – MUST READ BEFORE CONTINUING",
    lead: "Sweeter Trade shows stock signals, profit targets, and stop-loss levels as educational examples only.",
    bullets: [
      "Every idea is hypothetical — no real money is traded.",
      "We do not recommend buying or selling any stock.",
      "These are simulations, not predictions. You could lose money copying them.",
    ],
    short: [
      "Past or simulated performance ≠ future results.",
      "You are 100% responsible for your decisions. Consult a financial advisor if unsure.",
    ],
    full: [
      "Sweeter Trade is strictly an educational tool designed to help users learn about stock market analysis, technical indicators, historical data, charting techniques, and general trading concepts.",
      "All content — including signals, trade ideas, entry prices, stop-loss levels, profit targets, charts, webinars, and discussions — is provided solely for informational and educational purposes.",
      "This app does NOT provide investment advice or recommendations. We are not a registered investment adviser or broker-dealer. No content should be interpreted as a recommendation to buy, sell, or hold any security.",
      "Trading and investing carry substantial risk. You could lose some, all, or more than your investment. By continuing, you acknowledge these risks and agree that the developers of Sweeter Trade have no liability for any losses.",
    ],
    use: useClause("SweeterTrade"),
    checkbox: "I Understand – This Is Educational Content Only (Not Advice)",
  },
  qufolio: researchWording("qufolio", "QuFolio", "qf-2026-11-02"),
  trendstrat: researchWording("trendstrat", "TrendStrat", "ts-2026-11-02"),
};

/** The date a brand with no wording of its own last had its version moved. */
const FALLBACK_SINCE = "2026-11-02";

/**
 * This brand's disclaimer. A brand with no wording of its own gets QuFolio's
 * under its own name (`brandName`, else this build's when the key is this
 * build's, else the key) and a version of its own.
 */
export function disclaimerFor(brandKey: string, brandName?: string): Disclaimer {
  const own = DISCLAIMERS[brandKey];
  if (own) return own;
  const name = brandName ?? (brandKey === BRAND.key ? BRAND.identity.name : brandKey);
  const slug = brandKey.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 32) || "brand";
  return researchWording(slug, name, `${slug}-${FALLBACK_SINCE}`);
}

/** Whether this pointer can hover — decides "hover here" or "press and hold". */
export function canHover(media: (q: string) => { matches: boolean } = (q) => window.matchMedia(q)): boolean {
  try {
    return media("(hover: hover) and (pointer: fine)").matches;
  } catch {
    return false;
  }
}

/** Whether an acceptance at `acceptedAt` still holds at `now`. */
export function stillAccepted(acceptedAt: string | null, now: number = Date.now()): boolean {
  if (!acceptedAt) return false;
  const at = Date.parse(acceptedAt);
  if (Number.isNaN(at)) return false;
  return now - at < EVERY_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * The time of a remembered yes, so a current acceptance costs no request on
 * the next load.
 *
 * Only ever a shortcut: the server's record is the acceptance. Storage that
 * throws reads as "not remembered", which asks the server — never as accepted.
 */
const cacheKey = (version: string, actorKey: string) => `qf.disclaimer.${version}.${actorKey}`;

export function rememberedAccept(
  version: string,
  actorKey: string,
  storage: Pick<Storage, "getItem"> | null = safe(),
): string | null {
  try {
    return storage?.getItem(cacheKey(version, actorKey)) ?? null;
  } catch {
    return null;
  }
}

export function rememberAccept(
  version: string,
  actorKey: string,
  acceptedAt: string,
  storage: Pick<Storage, "setItem"> | null = safe(),
): void {
  try {
    storage?.setItem(cacheKey(version, actorKey), acceptedAt);
  } catch {
    /* the server holds it; the next load asks */
  }
}

function safe(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
